import {
  Dog,
  IDog,
  IIndicator,
  IInsight,
  IInsightDialog,
  IInsightReminder,
  IInsightTip,
  IInsightWidget,
  IProfile,
  MessageTemplating,
  Profile,
  WidgetType
} from '@jumpstart/core';
import { CLIMITS, CTEXT } from './DialogFormConfig';

const dog: IDog = Dog.defaultProps('default');
const profile: IProfile = Profile.defaultProps('unknown', 'unknown');
dog.name = 'Orin';
dog.gender = 'MALE';
profile.firstName = 'Patrick';

const dog2: IDog = Dog.defaultProps('default');
const profile2: IProfile = Profile.defaultProps('unknown', 'unknown');
dog2.name = 'Mila';
dog2.gender = 'FEMALE';
profile2.firstName = 'Luisa';

const dog3: IDog = Dog.defaultProps('default');
const profile3: IProfile = Profile.defaultProps('unknown', 'unknown');
dog3.name = 'Mochi';
dog3.gender = null;
profile3.firstName = 'Hunter';

export const testDog = dog;
export const testProfile = profile;
export const testDog2 = dog2;
export const testProfile2 = profile2;
export const testDog3 = dog3;
export const testProfile3 = profile3;

export interface IKeyword {
  name: string;
  id: string;
  example: string;
  examples: string[];
}

const _keywords: IKeyword[] = [
  ['{{DOG_NAME}}', 'Dog Name'],
  // ['{{DOG_NAME_APS}}', 'Dog Name (aps)'],
  ['{{DOG_PRONOUN}}', 'Dog Pronoun'],
  ['{{DOG_PRONOUN_CAP}}', 'Dog Pronoun (cap)'],
  // ['{{DOG_PRONOUN_APS}}', 'Dog Pronoun (aps)'],
  ['{{DOG_PRONOUN_POSSESSIVE}}', 'Dog Pronoun Possessive'],
  ['{{DOG_PRONOUN_POSSESSIVE_CAP}}', 'Dog Pronoun Possessive (cap)'],
  // ['{{DOG_PRONOUN_CAP_APS}}', 'Dog Pronoun (cap) (aps)'],
  ['{{DOG_PRONOUN_OBJ}}', 'Dog Pronoun Object'],
  ['{{DOG_PRONOUN_OBJ_CAP}}', 'Dog Pronoun Object (cap)'],
  // ['{{DOG_PRONOUN_OBJ_APS}}', 'Dog Pronoun Object (aps)'],
  ['{{DOG_PRONOUN_OBJ_POSSESSIVE}}', 'Dog Pronoun Object Possessive'],
  // ['{{DOG_PRONOUN_OBJ_CAP_APS}}', 'Dog Pronoun Object Possessive (aps)'],
  ['{{DOG_PRONOUN_OBJ_CAP_POSSESSIVE}}', 'Dog Pronoun Object Possessive (cap)'],
  ['{{USER_NAME}}', 'User Name']
].map((res: string[]) => {
  const e1: string = MessageTemplating.process(res[0], testDog, testProfile);
  const e2: string = MessageTemplating.process(res[0], testDog2, testProfile2);
  const e3: string = MessageTemplating.process(res[0], testDog3, testProfile3);
  return {
    id: res[0],
    name: res[1],
    example: `${e1}/${e2}/${e3}`,
    examples: [e1.toLowerCase(), e2.toLowerCase(), e3.toLowerCase()]
  };
});

export const KEYWORDS: IKeyword[] = [..._keywords];

const KEYWORD_EXAMPLES: string[] = KEYWORDS.reduce((accumulator: string[], keyword: IKeyword) => {
  for (const k of keyword.examples) {
    if (accumulator.indexOf(k) === -1 && ['their', 'them', 'they'].indexOf(k) === -1) {
      accumulator.push(k);
    }
  }
  return accumulator;
}, []);

export const splitWords = (text: string, numWords: number): string[] => {
  const words = text.split(' ');
  const lines: string[][] = [[]];
  while (words.length > 0) {
    const next: string | undefined = words.shift();
    if (!next) {
      continue;
    }
    for (let i: number = 0; i < lines.length; i++) {
      // if last index and full, add a array
      if (i === lines.length - 1 && lines[i].length === numWords) {
        lines.push([]);
      } else if (lines[i].length < numWords) {
        lines[i].push(next);
        break;
      }
    }
  }
  return lines.map((line: string[]) => line.join(' '));
};

export const isCompletion = (completionCriteria: any): boolean => {
  return !!(completionCriteria && typeof completionCriteria.value === 'number');
};

export type IValidation = { [path: string]: string[] };

export interface IValidationData {
  warnings: IValidation;
  numWarnings: number;
  errors: IValidation;
  numErrors: number;
}

class Validation {
  public static append(path: string, message: string, target: IValidation): void {
    if (target.hasOwnProperty(path)) {
      target[path].push(message);
    } else {
      target[path] = [message];
    }
  }
}

export const validateDefault = (): IValidationData => {
  return {
    warnings: {},
    errors: {},
    numWarnings: 0,
    numErrors: 0
  };
};


export const validateInsight = (insight: IInsight): IValidationData => {
  const warnings: IValidation = {};
  const errors: IValidation = {};
  if (insight.content.indicators.length === 0) {
    Validation.append('indicators', 'Event: Empty event, no indicators found', errors);
  }
  if (!insight.description) {
    Validation.append('description', 'Event: missing description, user won\'t know what this event is about', warnings);
  }
  if (!insight.severity) {
    Validation.append('severity', 'Event: missing severity', warnings);
  }

  if (insight.description && insight.description.length > CLIMITS.body) {
    Validation.append(`body`, `Event: over text limit ${CTEXT(insight.description, 'body')}`, errors);
  }
  if (insight.highlights) {
    insight.highlights.forEach((highlight: string, index: number) => {
      if (highlight.length > CLIMITS.highlights) {
        Validation.append(`highlights[${index}]`, `Event: over text limit ${CTEXT(highlight, 'highlights')}`, errors);
      }
    });
  }
  if (insight.tips) {
    insight.tips.forEach((tip: IInsightTip, index: number) => {
      if (tip.message.length > CLIMITS.tips) {
        Validation.append(`tips[${index}].message`, `Event: over text limit ${CTEXT(tip.message, 'tips')}`, errors);
      }
    });
  }
  if (insight.widgets) {
    insight.widgets.forEach((widget: IInsightWidget, index: number) => {
      if (!widget.widget) {
        Validation.append(`widgets[${index}].widget`, 'Event: No widget type provided', errors);
      }
      if (widget.purpose && widget.purpose.length > CLIMITS.body) {
        Validation.append(`widgets[${index}].purpose`, `Event: over text limit ${CTEXT(widget.purpose, 'body')}`, warnings);
      }
    });
  }

  ['name', 'description']
    .forEach((key: string) => {
      if ((insight as any)[key]) {
        checkKeywordProblems((insight as any)[key])
          .map((e: string) => `${key} ${e}`)
          .forEach((e: string) => {
            Validation.append(key, `Event: ${e}`, warnings);
          });
      }
    });

  for (let i: number = 0; i < insight.content.indicators.length; i++) {
    const indicator: IIndicator = insight.content.indicators[i];
    const results: IValidationData = validateIndicator(indicator);
    Object.keys(results.warnings)
      .forEach((key: string) => {
        Validation.append(`indicators[${i}].${key}`, `Indicator: ${results.warnings[key]}`, warnings);
      });
    Object.keys(results.errors)
      .forEach((key: string) => {
        Validation.append(`indicators[${i}].${key}`, `Indicator: ${results.errors[key]}`, errors);
      });
  }


  return {
    errors,
    warnings,
    numWarnings: Object.keys(warnings).length,
    numErrors: Object.keys(errors).length,
  };
};

export const validateIndicator = (indicator: IIndicator): IValidationData => {
  const warnings: IValidation = {};
  const errors: IValidation = {};

  if (!indicator.dialog) {
    Validation.append('dialog', 'Indicator: no dialog present', errors);
  }
  if (indicator.dialog && !indicator.dialog.responses) {
    Validation.append('dialog.responses', 'Indicator: no dialog responses', errors);
  }
  if (indicator.dialog && !!indicator.dialog.responses && Object.keys(indicator.dialog.responses).length === 0) {
    Validation.append('dialog.responses', 'Indicator: no dialog responses', errors);
  }
  // if (indicator.description) {
  //   warnings.push('Missing description, user won\'t know what this step is about');
  // }

  if (indicator.description && indicator.description.length > CLIMITS.body) {
    Validation.append(`body`, `Indicator: over text limit ${CTEXT(indicator.description, 'body')}`, errors);
  }

  ['name', 'description']
    .forEach((key: string) => {
      if ((indicator as any)[key]) {
        checkKeywordProblems((indicator as any)[key])
          .map((e: string) => `${key} ${e}`)
          .forEach((e: string) => {
            Validation.append(key, `Indicator: ${e}`, warnings);
          });
      }
    });

  // Get errors from nodes
  const nodes: IInsightDialog[] = [];
  const traverse = (currentNode: IInsightDialog, answer?: string | null, parentNode?: IInsightDialog) => {
    nodes.push(currentNode);
    const results: IValidationData = validateDialogNode(currentNode, answer, parentNode);
    Object.keys(results.warnings)
      .forEach((key: string) => {
        Validation.append(`dialog...dialog.${key}`, `Indicator: ${results.warnings[key]}`, warnings);
      });
    Object.keys(results.errors)
      .forEach((key: string) => {
        Validation.append(`dialog...dialog.${key}`, `Indicator: ${results.errors[key]}`, errors);
      });
    if (currentNode.responses) {
      Object.keys(currentNode.responses)
        .forEach((_answer: string) => traverse((currentNode.responses as any)[_answer], _answer, currentNode));
    } else {
      // If no more responses, it must have exit
      if (!isCompletion(currentNode.completionCriteria)) {
        Validation.append('dialog.completionCriteria', 'Indicator: Flow missing completion event', errors);
      }
    }
  };

  if (indicator.dialog) {
    traverse(indicator.dialog);
  }

  nodes.forEach((node: IInsightDialog) => {
    if (!node.id) {
      Validation.append(`dialog.id`, `Indicator: Dialog id is missing`, errors);
    }
    const filtered = nodes.filter((n: IInsightDialog) => node.id === n.id);
    if (filtered.length > 1) {
      Validation.append(`dialog.id`, `Indicator: Dialog id (${node.id}) is not unique`, errors);
    }
  });

  // All nodes must exit

  return {
    errors,
    warnings,
    numWarnings: Object.keys(warnings).length,
    numErrors: Object.keys(errors).length,
  };
};

/**
 *
 * @param currentNode - node to validate
 * @param link - the link between currentNode and parentNode (answer)
 * @param parentNode - parent node of currentNode
 * @param comprehensive - do more complex validation
 */
export const validateDialogNode = (
  currentNode: IInsightDialog,
  link?: string | null,
  parentNode?: IInsightDialog,
  comprehensive: boolean = true
): IValidationData => {
  const warnings: IValidation = {};
  const errors: IValidation = {};

  if (!currentNode.id) {
    Validation.append('id', 'Missing identifier', errors);
  }

  // Not a completion event
  if (!isCompletion(currentNode.completionCriteria)) {
    // Must have a body if question is not inline
    if (!currentNode.body && !currentNode.questionInline) {
      Validation.append('body', 'Dialog: No action provided (non-completion node)', errors);
    }
    // Must have a question if not a completion event
    if (!currentNode.question) {
      Validation.append('question', 'Dialog: No question provided (non-completion node)', errors);
    }

    // Instructions should be provided if not a completion event
    if (!currentNode.highlights || currentNode.highlights.length === 0) {
      Validation.append('highlights', 'Dialog: No instructions provided', warnings);
    }

    // if (!currentNode.details) {
    //   warnings.push('Missing details, user cannot learn more about this event');
    // }

    // warnings.push('Missing sources, user won\'t know where our methodology came from');

  }

  // Must have an answer except for the root node
  if (!link && !!parentNode) {
    Validation.append('answer', 'No answer response provided', errors);
  }

  if (comprehensive) {

    // Content Fields
    ['response', 'body', 'question', 'header', 'details']
      .forEach((key: string) => {
        if ((currentNode as any)[key]) {
          checkKeywordProblems((currentNode as any)[key])
            .map((e: string) => `${key} ${e}`)
            .forEach((e: string) => {
              Validation.append(key, `Dialog: ${e}`, warnings);
            });
        }
      });
    if (currentNode.reminders) {
      currentNode.reminders.forEach((r: IInsightReminder, i: number) => {
        checkKeywordProblems(r.message)
          .forEach((e: string) => {
            Validation.append(`reminders[${i}]`, `Dialog: ${e}`, warnings);
          });
      });
    }
    if (currentNode.highlights) {
      currentNode.highlights.forEach((s: string, i: number) => {
        if (s.toLowerCase().indexOf('repeat') > -1) {
          Validation.append(`highlights[${i}]`, `Dialog: repeat in highshould, use .reminders field instead`, warnings);
        }
        checkKeywordProblems(s)
          .forEach((e: string) => {
            Validation.append(`highlights[${i}]`, `Dialog: ${e}`, warnings);
          });
      });
    }
    if (currentNode.widgets) {
      currentNode.widgets.forEach((w: IInsightWidget, i: number) => {
        if (w.purpose) {
          checkKeywordProblems(w.purpose)
            .forEach((e: string) => {
              Validation.append(`widgets[${i}].purpose`, `Dialog: ${e}`, warnings);
            });
        }
        w.requirements.forEach((s: string, ii: number) => {
          checkKeywordProblems(s)
            .forEach((e: string) => {
              Validation.append(`widgets[${i}].requirements[${ii}]`, `Dialog: ${e}`, warnings);
            });
        });
      });
    }
    if (currentNode.tips) {
      currentNode.tips.forEach((t: IInsightTip, i: number) => {
        checkKeywordProblems(t.message)
          .forEach((e: string) => {
            Validation.append(`tips[${i}].message`, `Dialog: ${e}`, warnings);
          });
      });
    }
  }

  if (currentNode.body && currentNode.body.length > CLIMITS.body) {
    Validation.append(`body`, `Dialog: over text limit ${CTEXT(currentNode.body, 'body')}`, errors);
  }
  if (currentNode.question && currentNode.question.length > CLIMITS.question) {
    Validation.append(`body`, `Dialog: over text limit ${CTEXT(currentNode.question, 'question')}`, errors);
  }
  if (currentNode.questionShort && currentNode.questionShort.length > CLIMITS.questionShort) {
    Validation.append(`body`, `Dialog: over text limit ${CTEXT(currentNode.questionShort, 'questionShort')}`, errors);
  }
  if (currentNode.highlights) {
    currentNode.highlights.forEach((highlight: string, index: number) => {
      if (highlight.length > CLIMITS.highlights) {
        Validation.append(`highlights[${index}]`, `Dialog: over text limit ${CTEXT(highlight, 'highlights')}`, errors);
      }
    });
  }
  if (currentNode.tips) {
    currentNode.tips.forEach((tip: IInsightTip, index: number) => {
      if (tip.message.length > CLIMITS.tips) {
        Validation.append(`tips[${index}].message`, `Dialog: over text limit ${CTEXT(tip.message, 'tips')}`, errors);
      }
    });
  }

  if (currentNode.widgets) {
    currentNode.widgets.forEach((widget: IInsightWidget, index: number) => {
      if (Object.keys(WidgetType).indexOf(widget.widget) === -1 ) {
        Validation.append(`widgets[${index}].widget`, 'Dialog: No widget type provided', errors);
      }
      if (widget.purpose && widget.purpose.length > CLIMITS.body) {
        Validation.append(`widgets[${index}].purpose`, `Dialog: over text limit ${CTEXT(widget.purpose, 'body')}`, errors);
      }
    });
  }

  return {
    errors,
    warnings,
    numWarnings: Object.keys(warnings).length,
    numErrors: Object.keys(errors).length,
  };
};

const checkKeywordProblems = (text: string): string[] => {
  const errors: string[] = [];
  const words: string[] = text.toLowerCase().split(' ');
  for (const keyword of KEYWORD_EXAMPLES) {
    if (words.indexOf(keyword) > -1) {
      errors.push(`Word "${keyword}" used without keyword identifier`);
    }
  }
  return errors;
};
